import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import axiosInstance from 'utils/axiosUtil';
import { LocationsUtil } from 'utils';
import { FindLocationDialog } from 'locations';

const styles = (theme) => ({
  field: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },
  locationLink: {
    fontSize: 13,
    whiteSpace: 'nowrap',
    padding: theme.spacing(0.5, 1),
    minWidth: 0,
    flexShrink: 0,
  },
  entryTabs: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  odoLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: theme.spacing(1),
  },
  mileageReadonly: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  roundTripHint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(3),
  },
});

const EMPTY_FORM = {
  date_of_trip:     null, // will default to today on open
  trip_start:       '',
  trip_destination: '',
  is_round_trip:    false,
  is_business:      true,
  purpose:          '',
  mileage:          '',
  start_odometer:   '',
  end_odometer:     '',
  useOdometer:      false,
};

class MileageDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      form:                { ...EMPTY_FORM },
      saving:              false,
      saveError:           null,
      locationPickerOpen:  false,
      locationPickerTarget: null, // 'trip_start' | 'trip_destination'
      assignedUserId:      null,
    };
  }

  componentDidMount() {
    this._resetForm();
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this._resetForm();
    }
  }

  _resetForm() {
    this.setState({ saveError: null, saving: false, assignedUserId: this.props.defaultUserId || null });
    const { log } = this.props;
    if (log) {
      this.setState({
        form: {
          date_of_trip:     log.date_of_trip ? new Date(log.date_of_trip) : new Date(),
          trip_start:       log.trip_start       || '',
          trip_destination: log.trip_destination || '',
          is_round_trip:    log.is_round_trip    ?? false,
          is_business:      log.is_business      ?? true,
          purpose:          log.purpose          || '',
          mileage:          log.mileage          || '',
          start_odometer:   log.start_odometer != null ? String(log.start_odometer) : '',
          end_odometer:     log.end_odometer   != null ? String(log.end_odometer)   : '',
          useOdometer:      log.start_odometer != null || log.end_odometer != null,
        },
      });
    } else {
      const { defaultTripDestination } = this.props;
      const lastTripStart = localStorage.getItem('mileage_last_trip_start') || '';
      this.setState({
        form: {
          ...EMPTY_FORM,
          date_of_trip:     new Date(),
          trip_start:       lastTripStart,
          trip_destination: defaultTripDestination || '',
        },
      });
    }
  }

  openLocationPicker = (target) => {
    this.setState({ locationPickerOpen: true, locationPickerTarget: target });
  };

  onLocationSelected = (location) => {
    const { locationPickerTarget } = this.state;
    if (location && locationPickerTarget) {
      const formatted = LocationsUtil.formatFullNameAndAddress(location);
      this.onChange(locationPickerTarget, formatted);
    }
    this.setState({ locationPickerOpen: false, locationPickerTarget: null });
  };

  onChange = (field, value) => {
    this.setState((prev) => ({
      form: { ...prev.form, [field]: value },
    }));
  };

  /** Computed mileage when using odometer fields */
  _computedMileage() {
    const { start_odometer, end_odometer } = this.state.form;
    const s = parseFloat(start_odometer);
    const e = parseFloat(end_odometer);
    if (!isNaN(s) && !isNaN(e) && e >= s) {
      return (e - s).toFixed(1);
    }
    return null;
  }

  _isFutureDate(date) {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d > today;
  }

  canSave() {
    const { form, assignedUserId } = this.state;
    const { isAdmin, log } = this.props;
    if (!form.date_of_trip) return false;
    if (this._isFutureDate(form.date_of_trip)) return false;
    // Admins must assign to a user when creating a new log.
    if (isAdmin && !log && !assignedUserId) return false;
    if (form.useOdometer) {
      const computed = this._computedMileage();
      return computed !== null;
    }
    return parseFloat(form.mileage) > 0;
  }

  onSave = () => {
    const { clientId, log, activityNoteId, onSaved, onClose, isAdmin } = this.props;
    const { form, assignedUserId } = this.state;

    this.setState({ saving: true, saveError: null });

    const payload = {
      date_of_trip:     form.date_of_trip instanceof Date
        ? form.date_of_trip.toISOString().split('T')[0]
        : form.date_of_trip,
      trip_start:       form.trip_start       || null,
      trip_destination: form.trip_destination || null,
      is_round_trip:    form.is_round_trip,
      is_business:      form.is_business,
      purpose:          form.purpose          || null,
      activity_note_id: activityNoteId        || null,
    };

    // Admins creating a log must assign it to a user.
    if (isAdmin && !log && assignedUserId) {
      payload.user_id = assignedUserId;
    }

    if (form.useOdometer) {
      payload.start_odometer = parseFloat(form.start_odometer) || null;
      payload.end_odometer   = parseFloat(form.end_odometer)   || null;
    } else {
      payload.mileage = parseFloat(form.mileage);
    }

    const mileageValue = form.useOdometer
      ? (parseFloat(form.end_odometer) - parseFloat(form.start_odometer)).toFixed(1)
      : String(parseFloat(form.mileage));

    const request = log
      ? axiosInstance.put(`/v2/mileage/logs/${log.id}`, payload)
      : axiosInstance.post('/v2/mileage/logs', payload, { params: { clientId } });

    request.then((response) => {
      // Persist the trip start so it pre-fills on the next new log.
      if (payload.trip_start) {
        localStorage.setItem('mileage_last_trip_start', payload.trip_start);
      }
      const id = response?.data?.id || null;
      if (onSaved) onSaved({
        id,
        date_of_trip:     payload.date_of_trip,
        trip_start:       payload.trip_start,
        trip_destination: payload.trip_destination,
        is_round_trip:    payload.is_round_trip,
        is_business:      payload.is_business,
        purpose:          payload.purpose,
        mileage:          mileageValue,
        start_odometer:   form.useOdometer ? (parseFloat(form.start_odometer) || null) : null,
        end_odometer:     form.useOdometer ? (parseFloat(form.end_odometer)   || null) : null,
      });
      if (onClose) onClose();
    }).catch((err) => {
      console.error('MileageDialog save failed', err);
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'An unexpected error occurred. Please try again.';
      this.setState({ saving: false, saveError: message });
    });
  };

  render() {
    const { open, onClose, onDelete, classes, log, isAdmin, clientUsers } = this.props;
    const { form, saving, saveError, locationPickerOpen, assignedUserId } = this.state;
    const computedMileage = this._computedMileage();
    const isEdit = !!log;
    const isFutureDate = this._isFutureDate(form.date_of_trip);

    // Non-admin users available for assignment (exclude admins from the list)
    const assignableUsers = (clientUsers || []).filter((u) => !u.is_admin);

    return (
      <>
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>{isEdit ? 'Edit Mileage' : 'Log Mileage'}</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 360 }}>

          {/* Admin: Assign to User (new logs only) */}
          {isAdmin && !isEdit && (
            <FormControl variant='outlined' fullWidth sx={{ mb: 2 }} size='small'>
              <InputLabel shrink id='mileage-assign-user-label'>
                Assign to User *
              </InputLabel>
              <Select
                notched
                labelId='mileage-assign-user-label'
                id='mileage-assign-user-select'
                label='Assign to User *'
                value={assignedUserId || ''}
                onChange={(e) => this.setState({ assignedUserId: e.target.value })}
                displayEmpty
              >
                <MenuItem value='' disabled>
                  <em>Select a user…</em>
                </MenuItem>
                {assignableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Date of Trip */}
          <DatePicker
            label='Date of Trip'
            value={form.date_of_trip}
            onChange={(val) => this.onChange('date_of_trip', val)}
            slotProps={{
              textField: {
                className: classes.field,
                size: 'small',
                required: true,
                error: isFutureDate,
                helperText: isFutureDate ? 'Date cannot be in the future' : undefined,
              },
            }}
          />

          {/* Trip Start */}
          <div className={classes.fieldRow}>
            <TextField
              label='Trip Start'
              value={form.trip_start}
              onChange={(e) => this.onChange('trip_start', e.target.value)}
              size='small'
              placeholder='Address, office, city…'
              style={{ flex: 1 }}
            />
            <Button
              size='small'
              color='primary'
              className={classes.locationLink}
              onClick={() => this.openLocationPicker('trip_start')}
            >
              + Location
            </Button>
          </div>

          {/* Trip Destination */}
          <div className={classes.fieldRow}>
            <TextField
              label='Trip Destination'
              value={form.trip_destination}
              onChange={(e) => this.onChange('trip_destination', e.target.value)}
              size='small'
              placeholder='Address, office, city…'
              style={{ flex: 1 }}
            />
            <Button
              size='small'
              color='primary'
              className={classes.locationLink}
              onClick={() => this.openLocationPicker('trip_destination')}
            >
              + Location
            </Button>
          </div>

          {/* Round Trip */}
          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_round_trip}
                onChange={(e) => this.onChange('is_round_trip', e.target.checked)}
                size='small'
              />
            }
            label='Round trip'
            sx={{ mb: 0 }}
          />
          <Typography className={classes.roundTripHint}>
            Check for round trip. This is for tracking purposes and does not double the mileage.
          </Typography>

          {/* Purpose */}
          <TextField
            label='Purpose'
            value={form.purpose}
            onChange={(e) => this.onChange('purpose', e.target.value)}
            className={classes.field}
            size='small'
            multiline
            minRows={2}
          />

          {/* Mileage / Odometer tabs */}
          <Tabs
            value={form.useOdometer ? 'odometer' : 'mileage'}
            onChange={(_e, val) => this.onChange('useOdometer', val === 'odometer')}
            className={classes.entryTabs}
          >
            <Tab label='Mileage' value='mileage' disableRipple />
            <Tab label='Odometer' value='odometer' disableRipple />
          </Tabs>

          {form.useOdometer ? (
            <>
              <Typography className={classes.odoLabel}>
                Mileage will be calculated from start → end odometer.
              </Typography>
              <div className={classes.row}>
                <TextField
                  label='Start Odometer'
                  type='number'
                  value={form.start_odometer}
                  onChange={(e) => this.onChange('start_odometer', e.target.value)}
                  size='small'
                  required
                  style={{ flex: 1 }}
                  inputProps={{ min: 0 }}
                  sx={{
                    '& input[type=number]': { MozAppearance: 'textfield' },
                    '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                    '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                  }}
                />
                <TextField
                  label='End Odometer'
                  type='number'
                  value={form.end_odometer}
                  onChange={(e) => this.onChange('end_odometer', e.target.value)}
                  size='small'
                  required
                  style={{ flex: 1 }}
                  error={
                    form.end_odometer !== '' &&
                    form.start_odometer !== '' &&
                    parseFloat(form.end_odometer) < parseFloat(form.start_odometer)
                  }
                  helperText={
                    form.end_odometer !== '' &&
                    form.start_odometer !== '' &&
                    parseFloat(form.end_odometer) < parseFloat(form.start_odometer)
                      ? 'Must be greater than start'
                      : undefined
                  }
                  inputProps={{ min: 0 }}
                  sx={{
                    '& input[type=number]': { MozAppearance: 'textfield' },
                    '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                    '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                  }}
                />
              </div>
              {computedMileage !== null && (
                <Typography variant='body2' sx={{ mt: 1 }} className={classes.mileageReadonly}>
                  Calculated miles: <strong>{computedMileage}</strong>
                </Typography>
              )}
            </>
          ) : (
            <TextField
              label='Miles'
              type='number'
              value={form.mileage}
              onChange={(e) => this.onChange('mileage', e.target.value)}
              className={classes.field}
              size='small'
              required
              inputProps={{ min: 0, step: 0.1 }}
              sx={{
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
              }}
            />
          )}
        </DialogContent>
        {saveError && (
          <Alert severity='error' sx={{ mx: 2, mb: 1 }} onClose={() => this.setState({ saveError: null })}>
            {saveError}
          </Alert>
        )}
        <DialogActions>
          {isEdit && onDelete && (
            <Button
              onClick={() => onDelete(log)}
              color='error'
              disabled={saving}
            >
              Delete
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button onClick={onClose} color='primary' disabled={saving}>Cancel</Button>
          <Button
            onClick={this.onSave}
            variant='contained'
            color='primary'
            disabled={saving || !this.canSave()}
            startIcon={saving ? <CircularProgress size={16} color='inherit' /> : null}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {locationPickerOpen && (
        <FindLocationDialog
          open={locationPickerOpen}
          onSelect={this.onLocationSelected}
          onClose={() => this.setState({ locationPickerOpen: false, locationPickerTarget: null })}
        />
      )}
      </>
    );
  }
}

MileageDialog.propTypes = {
  classes:        PropTypes.object.isRequired,
  open:           PropTypes.bool.isRequired,
  onClose:        PropTypes.func.isRequired,
  onSaved:        PropTypes.func,
  /** Called with the log object when the user clicks Delete (edit mode only). */
  onDelete:       PropTypes.func,
  clientId:       PropTypes.number,
  /** If provided, the saved log will be linked to this activity note id. */
  activityNoteId: PropTypes.number,
  /** If provided, the dialog operates in edit mode. */
  log:                    PropTypes.object,
  /** Pre-populates Trip Destination for new logs (ignored in edit mode). */
  defaultTripDestination: PropTypes.string,
  /** Pre-selects the assigned user dropdown (e.g. when opening from a note). */
  defaultUserId: PropTypes.number,
  /** When true, shows the "Assign to User" dropdown for admin-created logs. */
  isAdmin:        PropTypes.bool,
  /** List of users in the selected client. Used to populate the assign dropdown. */
  clientUsers:    PropTypes.array,
};

export default withStyles(styles)(MileageDialog);
